import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { RoutePathsEnum } from '@app/app.utils';
import { ErrorReasonEnum } from '@error/ENUM_ErrorReason';
import { SentryService } from '@shared-lib/services/sentry.service';
import { ToastService, ToastSeverityEnum } from '@shared-lib/services/toast.service';
import { TOAST_MESSAGE } from '@shared/constants/toast-messages';
import { getUserIdFromToken } from '@shared/utils/get-user-id-from-token';
import { EMPTY, Observable, Subject, throwError } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

const WHITE_LIST_401_MESSAGES = new Set<ErrorReasonEnum>([
  ErrorReasonEnum.AdvisorSignInInvalidCredentials,
  ErrorReasonEnum.AdvisorSignInTimeout,
  ErrorReasonEnum.AdvisorSignInEmailNotFound,
  ErrorReasonEnum.AdvisorSignInSSOAccountDoesntExist,
  ErrorReasonEnum.AdvisorUserResetPasswordInvalidCode,
  ErrorReasonEnum.AdvisorUserResetPasswordExpiredCode,
]);
@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly sentryService = inject(SentryService);
  private readonly error401Subject = new Subject<void>();
  private readonly error401$ = this.error401Subject.asObservable().pipe(debounceTime(200));

  constructor() {
    this.error401$.subscribe(() => {
      this.authService.logout();
      void this.router.navigate([RoutePathsEnum.Landing, RoutePathsEnum.Login]);
      this.toastService.showToast(ToastSeverityEnum.Error, TOAST_MESSAGE.ERROR.unauthorized);
    });
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 0 && error.status !== 401 && error.status !== 503) {
          this.sentryService.captureHttpError(request, error, getUserIdFromToken(this.authService.getToken()));
        }
        if (WHITE_LIST_401_MESSAGES.has(error.error?.reason)) {
          return throwError(() => error);
        }
        if (error.status === 401) {
          this.authService.logout();
          this.error401Subject.next();
          return EMPTY;
        }
        return throwError(() => error);
      }),
    );
  }
}
